/**
 * HARMONY ENGINE — Chord & Scale Explorer
 * Clean theory reference: no gamification, no sign-up prompts.
 */

'use strict';

const STORAGE_KEY = 'sa-tool-harmony';

class HarmonyEngine {
    constructor() {
        // ── State ────────────────────────────────────────────────────────────
        this.root        = 'C';
        this.scaleType   = 'major';
        this.extensions  = new Set();
        this.soundType   = 'piano';
        this.currentMode = 'explorer';

        // Progression Builder
        this.progression = [];
        this.isLooping   = false;
        this._playingProg = false;

        // Audio
        this.ctx        = null;
        this.masterGain = null;
        this.convolver  = null;
        this._activeNodes = [];

        // ── Chromatic notes ──────────────────────────────────────────────────
        this.notes = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];

        // ── Roman numeral helpers ────────────────────────────────────────────
        this.romanNumerals = ['I','II','III','IV','V','VI','VII'];

        // ── Heptatonic scales ────────────────────────────────────────────────
        this.scales = {
            // Major modes
            major:            { intervals: [0,2,4,5,7,9,11],  name: 'Major (Ionian)',       category: 'Major Modes' },
            dorian:           { intervals: [0,2,3,5,7,9,10],  name: 'Dorian',               category: 'Major Modes' },
            phrygian:         { intervals: [0,1,3,5,7,8,10],  name: 'Phrygian',             category: 'Major Modes' },
            lydian:           { intervals: [0,2,4,6,7,9,11],  name: 'Lydian',               category: 'Major Modes' },
            mixolydian:       { intervals: [0,2,4,5,7,9,10],  name: 'Mixolydian',           category: 'Major Modes' },
            aeolian:          { intervals: [0,2,3,5,7,8,10],  name: 'Natural Minor',        category: 'Major Modes' },
            locrian:          { intervals: [0,1,3,5,6,8,10],  name: 'Locrian',              category: 'Major Modes' },

            // Melodic Minor modes
            melodic_minor:    { intervals: [0,2,3,5,7,9,11],  name: 'Melodic Minor',        category: 'Melodic Minor' },
            dorian_b2:        { intervals: [0,1,3,5,7,9,10],  name: 'Dorian ♭2',            category: 'Melodic Minor' },
            lydian_augmented: { intervals: [0,2,4,6,8,9,11],  name: 'Lydian Augmented',     category: 'Melodic Minor' },
            lydian_dominant:  { intervals: [0,2,4,6,7,9,10],  name: 'Lydian Dominant',      category: 'Melodic Minor' },
            mixolydian_b6:    { intervals: [0,2,4,5,7,8,10],  name: 'Mixolydian ♭6',        category: 'Melodic Minor' },
            locrian_nat2:     { intervals: [0,2,3,5,6,8,10],  name: 'Locrian ♮2',           category: 'Melodic Minor' },
            altered:          { intervals: [0,1,3,4,6,8,10],  name: 'Altered',              category: 'Melodic Minor' },

            // Harmonic Minor modes
            harmonic_minor:   { intervals: [0,2,3,5,7,8,11],  name: 'Harmonic Minor',       category: 'Harmonic Minor' },
            locrian_nat6:     { intervals: [0,1,3,5,6,9,10],  name: 'Locrian ♮6',           category: 'Harmonic Minor' },
            ionian_sharp5:    { intervals: [0,2,4,5,8,9,11],  name: 'Ionian #5',            category: 'Harmonic Minor' },
            dorian_sharp4:    { intervals: [0,2,3,6,7,9,10],  name: 'Dorian #4',            category: 'Harmonic Minor' },
            phrygian_dominant:{ intervals: [0,1,4,5,7,8,10],  name: 'Phrygian Dominant',    category: 'Harmonic Minor' },
            lydian_sharp2:    { intervals: [0,3,4,6,7,9,11],  name: 'Lydian #2',            category: 'Harmonic Minor' },
            ultralocrian:     { intervals: [0,1,3,4,6,8,9],   name: 'Ultralocrian',         category: 'Harmonic Minor' },

            // Harmonic Major modes
            harmonic_major:   { intervals: [0,2,4,5,7,8,11],  name: 'Harmonic Major',       category: 'Harmonic Major' },
            dorian_b5:        { intervals: [0,2,3,5,6,9,10],  name: 'Dorian ♭5',            category: 'Harmonic Major' },
            phrygian_b4:      { intervals: [0,1,3,4,7,8,10],  name: 'Phrygian ♭4',          category: 'Harmonic Major' },
            lydian_b3:        { intervals: [0,2,3,6,7,9,11],  name: 'Lydian ♭3',            category: 'Harmonic Major' },
            mixolydian_b2:    { intervals: [0,1,4,5,7,9,10],  name: 'Mixolydian ♭2',        category: 'Harmonic Major' },
            lydian_aug_sharp2:{ intervals: [0,3,4,6,8,9,11],  name: 'Lydian Aug #2',        category: 'Harmonic Major' },
            locrian_bb7:      { intervals: [0,1,3,5,6,8,9],   name: 'Locrian ♭♭7',          category: 'Harmonic Major' },

            // Double Harmonic
            double_harmonic:  { intervals: [0,1,4,5,7,8,11],  name: 'Double Harmonic',      category: 'Double Harmonic' },
            hungarian_minor:  { intervals: [0,2,3,6,7,8,11],  name: 'Hungarian Minor',      category: 'Double Harmonic' },
            oriental:         { intervals: [0,1,4,5,6,9,10],  name: 'Oriental',             category: 'Double Harmonic' },

            // Neapolitan
            neapolitan_major: { intervals: [0,1,3,5,7,9,11],  name: 'Neapolitan Major',     category: 'Neapolitan' },
            neapolitan_minor: { intervals: [0,1,3,5,7,8,11],  name: 'Neapolitan Minor',     category: 'Neapolitan' },
            leading_whole_tone:{ intervals: [0,2,4,6,8,10,11], name: 'Leading Whole Tone',  category: 'Neapolitan' },

            // Persian / Arabic
            persian:          { intervals: [0,1,4,5,6,8,11],  name: 'Persian',              category: 'Persian/Arabic' },
            arabic:           { intervals: [0,1,4,5,7,8,11],  name: 'Arabic (Hijaz)',       category: 'Persian/Arabic' },
            byzantine:        { intervals: [0,1,4,5,7,8,11],  name: 'Byzantine',            category: 'Persian/Arabic' },

            // Romanian / Hungarian
            romanian_minor:   { intervals: [0,2,3,6,7,9,10],  name: 'Romanian Minor',       category: 'Romanian/Hungarian' },
            hungarian_major:  { intervals: [0,3,4,6,7,9,10],  name: 'Hungarian Major',      category: 'Romanian/Hungarian' },
            spanish_gypsy:    { intervals: [0,1,4,5,7,8,10],  name: 'Spanish Gypsy',        category: 'Romanian/Hungarian' },

            // Enigmatic / Exotic
            enigmatic:        { intervals: [0,1,4,6,8,10,11], name: 'Enigmatic',            category: 'Exotic' },
            enigmatic_minor:  { intervals: [0,1,3,6,8,10,11], name: 'Enigmatic Minor',      category: 'Exotic' },
            algerian:         { intervals: [0,2,3,6,7,8,11],  name: 'Algerian',             category: 'Exotic' },
            balinese:         { intervals: [0,1,3,7,8,10,11], name: 'Balinese',             category: 'Exotic' },
            javanese:         { intervals: [0,1,3,5,7,9,10],  name: 'Javanese',             category: 'Exotic' },

            // Blues / Jazz
            blues_7:          { intervals: [0,3,5,6,7,10,11], name: 'Blues (7-note)',       category: 'Blues/Jazz' },
            major_blues_7:    { intervals: [0,2,3,4,7,9,11],  name: 'Major Blues (7-note)', category: 'Blues/Jazz' },

            // Synthetic
            prometheus:       { intervals: [0,2,4,6,9,10,11], name: 'Prometheus',           category: 'Synthetic' },
            tritone:          { intervals: [0,1,4,6,7,10,11], name: 'Tritone',              category: 'Synthetic' },
        };

    }

    // ── Boot ─────────────────────────────────────────────────────────────────
    init() {
        const drawer = document.getElementById('ChordDetailDrawer');
        if (drawer && drawer.parentElement !== document.body) document.body.appendChild(drawer);
        this._restoreSettings();
        this.populateSelects();
        this.buildMatrix();
        this.bindEvents();
        this.updateScaleNotes();
        this._bindAudioStop();
    }

    // ── Settings persistence ─────────────────────────────────────────────────
    _saveSettings() {
        try {
            const data = {
                root:        this.root,
                scaleType:   this.scaleType,
                soundType:   this.soundType,
                extensions:  [...this.extensions],
            };
            window.flPreferenceStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (_) {}
    }

    _restoreSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            if (data && typeof data === 'object') {
                if (data.root && this.notes.includes(data.root))   this.root        = data.root;
                if (data.scaleType && this.scales[data.scaleType]) this.scaleType   = data.scaleType;
                if (data.soundType)                                this.soundType   = data.soundType;
                if (Array.isArray(data.extensions)) {
                    this.extensions = new Set(data.extensions.filter(e => ['7','9','11','13'].includes(e)));
                }
            }
        } catch (_) {}
    }

    // ── Stop all audio when tab is hidden ────────────────────────────────────
    _bindAudioStop() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) this._stopAllAudio();
        });
    }

    _stopAllAudio() {
        if (!this.ctx) return;
        this._activeNodes.forEach(n => {
            try { n.stop(); } catch (_) {}
        });
        this._activeNodes = [];
        this._playingProg  = false;
    }

    // ── Populate selects ─────────────────────────────────────────────────────
    populateSelects() {
        // Root
        const rootSelect = document.getElementById('HarmonyRootSelect');
        if (rootSelect) {
            rootSelect.innerHTML = '';
            this.notes.forEach(n => {
                const opt = document.createElement('option');
                opt.value = n;
                opt.textContent = n;
                if (n === this.root) opt.selected = true;
                rootSelect.appendChild(opt);
            });
        }

        // Scale
        const scaleSelect = document.getElementById('HarmonyScaleSelect');
        if (scaleSelect) {
            scaleSelect.innerHTML = '';
            const essentialKeys = ['major','dorian','phrygian','lydian','mixolydian','aeolian','locrian','melodic_minor','harmonic_minor','phrygian_dominant'];

            if (!essentialKeys.includes(this.scaleType)) this.scaleType = 'major';

            const essGroup = document.createElement('optgroup');
            essGroup.label = 'Essential Scales';
            essentialKeys.forEach(key => {
                if (!this.scales[key]) return;
                const opt = document.createElement('option');
                opt.value = key;
                opt.textContent = this.scales[key].name;
                if (key === this.scaleType) opt.selected = true;
                essGroup.appendChild(opt);
            });
            scaleSelect.appendChild(essGroup);

        }

        // Sound
        const soundSelect = document.getElementById('SoundSelect');
        if (soundSelect) soundSelect.value = this.soundType;

        // VL From Chord
        const vlFrom = document.getElementById('VLFromChord');
        if (vlFrom) {
            vlFrom.innerHTML = '';
            this.notes.forEach(n => {
                ['maj','min'].forEach(q => {
                    const opt = document.createElement('option');
                    opt.value = n + q;
                    opt.textContent = n + (q === 'min' ? 'm' : '');
                    vlFrom.appendChild(opt);
                });
            });
        }

        // Reflect extension toggles from restored state
        document.querySelectorAll('.hm-ext-toggle').forEach(btn => {
            const active = this.extensions.has(btn.dataset.ext);
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-pressed', String(active));
        });

    }

    // ── Build chord matrix ───────────────────────────────────────────────────
    buildMatrix() {
        const container = document.getElementById('ChordMatrix');
        if (!container) return;
        container.innerHTML = '';

        const rootIdx  = this.notes.indexOf(this.root);
        const scaleData = this.scales[this.scaleType] || this.scales.major;
        const intervals = scaleData.intervals;

        for (let degree = 0; degree < 7; degree++) {
            const chord     = this._getDiatonicChord(this.scaleType, degree, rootIdx);
            const quality   = chord.quality;
            const chordName = chord.name;
            const extLabel  = this._extensionSuffix();
            const qualClass = this._qualityClass(quality);
            const roman     = this._formatRoman(degree, quality);
            const noteArr   = chord.notes;

            const col = document.createElement('div');
            col.className = 'hm-chord-col';
            col.innerHTML = `
              <span class="hm-chord-col__roman">${roman}</span>
              <button
                class="hm-chord-col__btn hm-chord-col__btn--${qualClass}"
                data-chord="${chordName}"
                data-notes="${noteArr.join(',')}"
                data-degree="${degree}"
                aria-label="Play ${chordName} — ${this._qualityLabel(quality)}"
              >${chordName}${extLabel}</button>
              <span class="hm-chord-col__quality">${this._qualityLabel(quality)}</span>
              <button
                class="hm-chord-col__add"
                data-add="${chordName}"
                data-notes="${noteArr.join(',')}"
                aria-label="Add ${chordName} to progression"
              >+ Add</button>
            `;
            container.appendChild(col);
        }
    }

    // ── Interchange panels ───────────────────────────────────────────────────
    buildInterchangePanel() {
        const parallelModes = [
            { id: 'MajorChords',    scale: 'major' },
            { id: 'MinorChords',    scale: 'aeolian' },
            { id: 'DorianChords',   scale: 'dorian' },
            { id: 'MixolydianChords', scale: 'mixolydian' },
        ];
        parallelModes.forEach(({ id, scale }) => {
            const container = document.getElementById(id);
            if (!container) return;
            container.innerHTML = '';
            const rootIdx  = this.notes.indexOf(this.root);
            const scaleData = this.scales[scale];
            if (!scaleData) return;
            for (let i = 0; i < 7; i++) {
                const chord     = this._getDiatonicChord(scale, i, rootIdx);
                const chordName = chord.name;
                const quality   = chord.quality;
                const roman     = this._formatRoman(i, quality);
                const noteArr   = chord.notes;
                const btn = document.createElement('button');
                btn.className = 'hm-mode-chord-btn';
                btn.textContent = `${roman} — ${chordName}`;
                btn.dataset.chord = chordName;
                btn.dataset.notes = noteArr.join(',');
                btn.setAttribute('aria-label', `Play ${chordName} from ${scaleData.name}`);
                btn.addEventListener('click', () => {
                    this._playChord(noteArr);
                    this._announce(`${chordName} from ${scaleData.name}`);
                });
                container.appendChild(btn);
            }
        });
    }

    // ── Helpers ──────────────────────────────────────────────────────────────
    _getDiatonicChord(scaleKey, degree, tonicIdx) {
        const scale = this.scales[scaleKey] || this.scales.major;
        const intervals = scale.intervals;
        const absoluteInterval = (stackStep) => {
            const position = degree + stackStep;
            return intervals[position % 7] + (12 * Math.floor(position / 7));
        };
        const rootInterval = absoluteInterval(0);
        const third = absoluteInterval(2) - rootInterval;
        const fifth = absoluteInterval(4) - rootInterval;
        const qualityMap = {
            '4,7': 'maj',
            '3,7': 'min',
            '3,6': 'dim',
            '4,8': 'aug'
        };
        const quality = qualityMap[`${third},${fifth}`] || 'other';
        const stackSteps = [0, 2, 4];
        const extensionSteps = { '7': 6, '9': 8, '11': 10, '13': 12 };
        ['7', '9', '11', '13'].forEach(extension => {
            if (this.extensions.has(extension)) stackSteps.push(extensionSteps[extension]);
        });
        const notes = stackSteps.map(step => (tonicIdx + absoluteInterval(step)) % 12);
        const rootNote = this.notes[(tonicIdx + rootInterval) % 12];
        const name = quality === 'other'
            ? `${rootNote}(${third}–${fifth})`
            : this._formatChordName(rootNote, quality);
        return { name, notes, quality, third, fifth };
    }

    _formatChordName(note, quality) {
        const suffixes = { maj: '', min: 'm', dim: '°', aug: '+', dom: '' };
        return note + (suffixes[quality] ?? '');
    }

    _formatRoman(degree, quality) {
        const n = this.romanNumerals[degree] || String(degree + 1);
        if (quality === 'min' || quality === 'dim') return n.toLowerCase();
        return n;
    }

    _qualityClass(quality) {
        const map = { maj: 'major', min: 'minor', dim: 'dim', aug: 'aug', dom: 'dom', other: 'other' };
        return map[quality] || 'other';
    }

    _qualityLabel(quality) {
        const map = { maj: 'Major', min: 'Minor', dim: 'Diminished', aug: 'Augmented', dom: 'Dominant', other: 'Scale-tone stack' };
        return map[quality] || 'Scale-tone stack';
    }

    _extensionSuffix() {
        const active = ['7', '9', '11', '13'].filter(extension => this.extensions.has(extension));
        return active.length ? ` +${active.join('/')}` : '';
    }

    _getChordNotes(rootIdx, quality) {
        let ivls;
        switch (quality) {
            case 'maj': ivls = [0,4,7]; break;
            case 'min': ivls = [0,3,7]; break;
            case 'dim': ivls = [0,3,6]; break;
            case 'aug': ivls = [0,4,8]; break;
            case 'dom': ivls = [0,4,7]; break;
            default:    ivls = [0,4,7];
        }
        if (this.extensions.has('7')) {
            if (quality === 'maj')      ivls = [...ivls, 11];
            else if (quality === 'min') ivls = [...ivls, 10];
            else if (quality === 'dim') ivls = [...ivls,  9];
            else if (quality === 'dom') ivls = [...ivls, 10];
        }
        if (this.extensions.has('9'))  ivls = [...ivls, 14];
        if (this.extensions.has('11')) ivls = [...ivls, 17];
        if (this.extensions.has('13')) ivls = [...ivls, 21];
        return ivls.map(i => (rootIdx + i) % 12);
    }

    _chordNotesFromString(chordStr) {
        const match = chordStr.match(/^([A-G]#?)(maj|min)?$/);
        if (!match) return [0,4,7];
        const root    = match[1];
        const quality = match[2] === 'min' ? 'min' : 'maj';
        return this._getChordNotes(this.notes.indexOf(root), quality);
    }

    _announce(msg) {
        const el = document.getElementById('HmStatus');
        if (el) el.textContent = msg;
    }

    // ── Scale notes footer ───────────────────────────────────────────────────
    updateScaleNotes() {
        const el = document.getElementById('ScaleNotes');
        if (!el) return;
        const rootIdx   = this.notes.indexOf(this.root);
        const scaleData = this.scales[this.scaleType] || this.scales.major;
        const noteNames = scaleData.intervals.map(i => this.notes[(rootIdx + i) % 12]);
        el.textContent = `${this.root} ${scaleData.name}: ${noteNames.join(' – ')}`;
    }

    // ── Audio ────────────────────────────────────────────────────────────────
    _initAudio() {
        if (!this.ctx) {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.ctx.createGain();
            this.masterGain.gain.value = 0.4;

            // Simple synthetic reverb (IR)
            const impulseLen = Math.floor(this.ctx.sampleRate * 1.5);
            const impulse    = this.ctx.createBuffer(2, impulseLen, this.ctx.sampleRate);
            for (let c = 0; c < 2; c++) {
                const ch = impulse.getChannelData(c);
                for (let i = 0; i < impulseLen; i++) {
                    ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / impulseLen, 2);
                }
            }
            this.convolver = this.ctx.createConvolver();
            this.convolver.buffer = impulse;

            const dryGain = this.ctx.createGain();
            dryGain.gain.value = 0.7;
            const wetGain = this.ctx.createGain();
            wetGain.gain.value = 0.3;

            this.masterGain.connect(dryGain);
            dryGain.connect(this.ctx.destination);
            this.masterGain.connect(this.convolver);
            this.convolver.connect(wetGain);
            wetGain.connect(this.ctx.destination);
        }
        if (this.ctx.state === 'suspended') this.ctx.resume();
    }

    _playChord(noteIndices, duration = 0.9) {
        this._initAudio();
        const now = this.ctx.currentTime;

        noteIndices.forEach((noteIdx, i) => {
            // Spread notes across two octaves for a fuller voicing
            const octaveShift = i === 0 ? 0 : i < 3 ? 12 : 24;
            const midiNote    = 60 + noteIdx + octaveShift;
            const freq        = 440 * Math.pow(2, (midiNote - 69) / 12);

            const osc    = this.ctx.createOscillator();
            const gain   = this.ctx.createGain();
            const filter = this.ctx.createBiquadFilter();

            osc.type           = this.soundType === 'synth' ? 'sawtooth' : 'triangle';
            osc.frequency.value = freq;

            filter.type           = 'lowpass';
            filter.frequency.value = 2200;
            filter.Q.value         = 0.8;

            gain.gain.setValueAtTime(0, now);
            gain.gain.linearRampToValueAtTime(0.14, now + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

            osc.connect(filter);
            filter.connect(gain);
            gain.connect(this.masterGain);

            osc.start(now);
            osc.stop(now + duration + 0.05);

            this._activeNodes.push(osc);
            osc.addEventListener('ended', () => {
                const idx = this._activeNodes.indexOf(osc);
                if (idx !== -1) this._activeNodes.splice(idx, 1);
            });
        });
    }

    // ── Chord detail drawer ──────────────────────────────────────────────────
    showChordDetail(name, degreeIdx, notes) {
        const drawer   = document.getElementById('ChordDetailDrawer');
        const nameEl   = document.getElementById('DetailName');
        const degreeEl = document.getElementById('DetailDegree');
        const stackEl  = document.getElementById('DetailStack');
        const intentEl = document.getElementById('DetailIntent');
        const addBtn   = document.getElementById('DetailAddBtn');
        if (!drawer) return;

        if (nameEl)   nameEl.textContent   = name;
        if (degreeEl) degreeEl.textContent = this._formatRoman(degreeIdx, this._degreeQuality(degreeIdx));

        if (stackEl) {
            stackEl.innerHTML = '';
            notes.forEach((noteIdx, i) => {
                const pill = document.createElement('span');
                pill.className = 'hm-note-pill' + (i === 0 ? ' is-root' : '');
                pill.textContent = this.notes[noteIdx % 12];
                stackEl.appendChild(pill);
            });
        }

        if (intentEl) {
            const scaleName = (this.scales[this.scaleType] || this.scales.major).name;
            intentEl.textContent = `Built by stacking alternate notes of ${this.root} ${scaleName} from degree ${degreeIdx + 1}.`;
        }

        if (addBtn) {
            const fresh = addBtn.cloneNode(true);
            addBtn.parentNode.replaceChild(fresh, addBtn);
            fresh.addEventListener('click', () => {
                this.addToProgression(name, notes);
                this._closeDrawer();
            });
        }

        this._drawerReturnFocus = document.activeElement;
        drawer.removeAttribute('hidden');
        drawer.removeAttribute('inert');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('hm-drawer-open');
        window.FLToolDialogIsolation?.open(drawer);
        // Move focus to close button for accessibility
        const closeBtn = drawer.querySelector('.js-close-drawer[aria-label="Close"]');
        if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    }

    _degreeQuality(degreeIdx) {
        return this._getDiatonicChord(this.scaleType, degreeIdx, this.notes.indexOf(this.root)).quality;
    }

    _closeDrawer() {
        const drawer = document.getElementById('ChordDetailDrawer');
        if (!drawer || drawer.hasAttribute('hidden')) return;
        window.FLToolDialogIsolation?.close(drawer);
        drawer.setAttribute('hidden', '');
        drawer.setAttribute('inert', '');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('hm-drawer-open');
        const returnFocus = this._drawerReturnFocus;
        this._drawerReturnFocus = null;
        if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    }

    // ── Progression ──────────────────────────────────────────────────────────
    addToProgression(chordName, noteIndices) {
        this.progression.push({ name: chordName, notes: noteIndices });
        this.renderProgression();
        this._announce(`${chordName} added to progression`);
    }

    removeFromProgression(index) {
        this.progression.splice(index, 1);
        this.renderProgression();
    }

    clearProgression() {
        this.progression = [];
        this.renderProgression();
    }

    renderProgression() {
        const slots = document.getElementById('ProgressionSlots');
        if (!slots) return;
        slots.innerHTML = '';

        if (this.progression.length === 0) {
            const empty = document.createElement('span');
            empty.className = 'hm-prog-empty';
            empty.textContent = 'Click chords in the matrix to add them here.';
            slots.appendChild(empty);
            return;
        }

        this.progression.forEach((chord, i) => {
            const slot = document.createElement('div');
            slot.className = 'hm-prog-slot';
            slot.setAttribute('role', 'group');
            slot.setAttribute('aria-label', chord.name);
            const play = document.createElement('button');
            play.type = 'button';
            play.className = 'hm-prog-slot__play';
            play.textContent = chord.name;
            play.setAttribute('aria-label', `Play ${chord.name}`);
            play.addEventListener('click', () => this._playChord(chord.notes));
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'hm-prog-slot__remove';
            remove.dataset.index = String(i);
            remove.textContent = 'Remove';
            remove.setAttribute('aria-label', `Remove ${chord.name}`);
            slot.append(play, remove);
            slots.appendChild(slot);
        });

        slots.querySelectorAll('.hm-prog-slot__remove').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeFromProgression(parseInt(btn.dataset.index, 10));
            });
        });
    }

    async playProgression() {
        if (this.progression.length === 0 || this._playingProg) return;
        this._playingProg = true;

        const playBtn = document.getElementById('PlayProgressionBtn');
        const loopBtn = document.getElementById('LoopProgressionBtn');

        do {
            for (const chord of this.progression) {
                if (!this._playingProg) break;
                this._playChord(chord.notes);
                await new Promise(r => setTimeout(r, 900));
            }
        } while (this._playingProg && this.isLooping);

        this._playingProg = false;
        if (playBtn) playBtn.textContent = '▶ Play';
    }

    // ── Neo-Riemannian transforms ────────────────────────────────────────────
    applyTransform(chordStr, transform) {
        const match = chordStr.match(/^([A-G]#?)(maj|min)$/);
        if (!match) return chordStr;

        const root    = match[1];
        const quality = match[2];
        const idx     = this.notes.indexOf(root);
        let newRoot = root, newQuality = quality;

        switch (transform) {
            case 'P': // Parallel — flip quality, keep root
                newQuality = quality === 'maj' ? 'min' : 'maj';
                break;
            case 'R': // Relative
                if (quality === 'maj') { newRoot = this.notes[(idx + 9) % 12]; newQuality = 'min'; }
                else                   { newRoot = this.notes[(idx + 3) % 12]; newQuality = 'maj'; }
                break;
            case 'L': // Leading-tone
                if (quality === 'maj') { newRoot = this.notes[(idx + 4) % 12]; newQuality = 'min'; }
                else                   { newRoot = this.notes[(idx + 8) % 12]; newQuality = 'maj'; }
                break;
        }

        return newRoot + newQuality;
    }

    // ── Tab / panel switching ────────────────────────────────────────────────
    showModePanel(mode) {
        this.currentMode = mode;

        // Update tab aria attributes
        document.querySelectorAll('.hm-tab[data-mode]').forEach(tab => {
            const isActive = tab.dataset.mode === mode;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-pressed', String(isActive));
        });

        // Show / hide panels
        const panelMap = {
            'explorer':     null,                // matrix is always visible; no panel
            'builder':      'panel-builder',
            'interchange':  'panel-interchange',
            'voice-leading':'panel-voice-leading',
        };
        ['panel-builder','panel-interchange','panel-voice-leading'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.setAttribute('hidden', '');
        });

        const targetId = panelMap[mode];
        if (targetId) {
            const panel = document.getElementById(targetId);
            if (panel) {
                panel.removeAttribute('hidden');
                if (mode === 'interchange') this.buildInterchangePanel();
                if (mode === 'builder')     this.renderProgression();
            }
        }
    }

    // ── Event bindings ───────────────────────────────────────────────────────
    bindEvents() {
        // Root select
        document.getElementById('HarmonyRootSelect')?.addEventListener('change', (e) => {
            this.root = e.target.value;
            this.buildMatrix();
            this.updateScaleNotes();
            this._saveSettings();
        });

        // Scale select
        document.getElementById('HarmonyScaleSelect')?.addEventListener('change', (e) => {
            this.scaleType = e.target.value;
            this.buildMatrix();
            this.updateScaleNotes();
            if (this.currentMode === 'interchange') this.buildInterchangePanel();
            this._saveSettings();
        });

        // Extension toggles
        document.querySelectorAll('.hm-ext-toggle').forEach(btn => {
            btn.addEventListener('click', () => {
                const ext = btn.dataset.ext;
                if (this.extensions.has(ext)) this.extensions.delete(ext);
                else                           this.extensions.add(ext);
                btn.classList.toggle('is-active', this.extensions.has(ext));
                btn.setAttribute('aria-pressed', String(this.extensions.has(ext)));
                this.buildMatrix();
                this._saveSettings();
            });
        });

        // Sound select
        document.getElementById('SoundSelect')?.addEventListener('change', (e) => {
            this.soundType = e.target.value;
            this._saveSettings();
        });

        // Tabs
        document.querySelectorAll('.hm-tab[data-mode]').forEach(tab => {
            tab.addEventListener('click', () => this.showModePanel(tab.dataset.mode));
            tab.addEventListener('keydown', (event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const tabs = Array.from(document.querySelectorAll('.hm-tab[data-mode]'));
                const currentIndex = tabs.indexOf(tab);
                let nextIndex = currentIndex;
                if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
                if (event.key === 'Home') nextIndex = 0;
                if (event.key === 'End') nextIndex = tabs.length - 1;
                tabs[nextIndex].focus();
                tabs[nextIndex].click();
            });
        });

        // Chord matrix (delegated)
        document.getElementById('ChordMatrix')?.addEventListener('click', (e) => {
            const chordBtn = e.target.closest('.hm-chord-col__btn');
            const addBtn   = e.target.closest('.hm-chord-col__add');

            if (chordBtn) {
                const notes     = chordBtn.dataset.notes.split(',').map(Number);
                const chordName = chordBtn.dataset.chord;
                const degreeIdx = parseInt(chordBtn.dataset.degree, 10);
                this._playChord(notes);
                this.showChordDetail(chordName, degreeIdx, notes);
                this._announce(`${chordName} — ${this._qualityLabel(this._degreeQuality(degreeIdx))}`);
            }

            if (addBtn) {
                const notes     = addBtn.dataset.notes.split(',').map(Number);
                const chordName = addBtn.dataset.chord;
                this.addToProgression(chordName, notes);
            }
        });

        // Drawer close
        document.querySelectorAll('.js-close-drawer').forEach(el => {
            el.addEventListener('click', () => this._closeDrawer());
        });
        document.addEventListener('keydown', (e) => {
            const drawer = document.getElementById('ChordDetailDrawer');
            if (e.key === 'Escape' && drawer && !drawer.hasAttribute('hidden')) {
                e.preventDefault();
                this._closeDrawer();
                return;
            }
            if (e.key === 'Tab' && drawer && !drawer.hasAttribute('hidden')) {
                const focusables = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        });

        // Progression controls
        document.getElementById('PlayProgressionBtn')?.addEventListener('click', () => {
            if (this._playingProg) {
                this._playingProg = false;
                document.getElementById('PlayProgressionBtn').textContent = '▶ Play';
            } else {
                document.getElementById('PlayProgressionBtn').textContent = '■ Stop';
                this.playProgression().then(() => {
                    const btn = document.getElementById('PlayProgressionBtn');
                    if (btn) btn.textContent = '▶ Play';
                });
            }
        });

        document.getElementById('LoopProgressionBtn')?.addEventListener('click', (e) => {
            this.isLooping = !this.isLooping;
            e.currentTarget.setAttribute('aria-pressed', String(this.isLooping));
            e.currentTarget.classList.toggle('is-active', this.isLooping);
        });

        document.getElementById('ClearProgressionBtn')?.addEventListener('click', () => {
            this._playingProg = false;
            this.clearProgression();
        });

        // Common progression library
        document.querySelectorAll('[data-prog]').forEach(btn => {
            btn.addEventListener('click', () => {
                this.clearProgression();
                const prog = btn.dataset.prog.split(',');
                prog.forEach(d => {
                    const degree = parseInt(d.replace('b', ''), 10) - 1;
                    if (degree < 0 || degree > 6) return;
                    const rootIdx   = this.notes.indexOf(this.root);
                    const chord = this._getDiatonicChord(this.scaleType, degree, rootIdx);
                    this.addToProgression(chord.name, chord.notes);
                });
            });
        });

        // Voice Leading transforms
        document.querySelectorAll('[data-transform]').forEach(btn => {
            btn.addEventListener('click', () => {
                const fromChord = document.getElementById('VLFromChord')?.value || 'Cmaj';
                const result    = this.applyTransform(fromChord, btn.dataset.transform);
                const resultEl  = document.getElementById('VLResult');
                if (resultEl) resultEl.textContent = result.replace('maj','').replace('min','m') || result;

                // Show voice motion
                const motionEl = document.getElementById('VoiceMotionDisplay');
                if (motionEl) {
                    const fromNotes = this._chordNotesFromString(fromChord);
                    const toNotes   = this._chordNotesFromString(result);
                    const fromNames = fromNotes.map(n => this.notes[n % 12]);
                    const toNames   = toNotes.map(n => this.notes[n % 12]);
                    motionEl.textContent = `${fromNames.join(' – ')}  →  ${toNames.join(' – ')}`;
                }

                // Play source, then result
                this._playChord(this._chordNotesFromString(fromChord), 0.5);
                setTimeout(() => this._playChord(this._chordNotesFromString(result), 0.9), 600);
                this._announce(`${btn.dataset.transform} transform: ${fromChord} → ${result}`);
            });
        });

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            const drawer = document.getElementById('ChordDetailDrawer');
            if (drawer && !drawer.hasAttribute('hidden')) return;
            if (e.target.closest('input, select, textarea, button, a, [contenteditable="true"], [role="button"]')) return;

            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    document.getElementById('PlayProgressionBtn')?.click();
                    break;

                case 'Digit1': case 'Digit2': case 'Digit3':
                case 'Digit4': case 'Digit5': case 'Digit6': case 'Digit7': {
                    e.preventDefault();
                    const idx = parseInt(e.code.replace('Digit',''), 10) - 1;
                    const cols = document.querySelectorAll('.hm-chord-col');
                    if (cols[idx]) {
                        const cb = cols[idx].querySelector('.hm-chord-col__btn');
                        if (cb) cb.click();
                    }
                    break;
                }

                case 'ArrowLeft': {
                    e.preventDefault();
                    const sel = document.getElementById('HarmonyRootSelect');
                    if (sel) {
                        sel.selectedIndex = (sel.selectedIndex - 1 + sel.options.length) % sel.options.length;
                        sel.dispatchEvent(new Event('change'));
                    }
                    break;
                }

                case 'ArrowRight': {
                    e.preventDefault();
                    const sel = document.getElementById('HarmonyRootSelect');
                    if (sel) {
                        sel.selectedIndex = (sel.selectedIndex + 1) % sel.options.length;
                        sel.dispatchEvent(new Event('change'));
                    }
                    break;
                }

                case 'ArrowUp': {
                    e.preventDefault();
                    const sel = document.getElementById('HarmonyScaleSelect');
                    if (sel) {
                        sel.selectedIndex = (sel.selectedIndex - 1 + sel.options.length) % sel.options.length;
                        sel.dispatchEvent(new Event('change'));
                    }
                    break;
                }

                case 'ArrowDown': {
                    e.preventDefault();
                    const sel = document.getElementById('HarmonyScaleSelect');
                    if (sel) {
                        sel.selectedIndex = (sel.selectedIndex + 1) % sel.options.length;
                        sel.dispatchEvent(new Event('change'));
                    }
                    break;
                }
            }
        });
    }
}

// ── Bootstrap ────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
    window.flRunWhenPrivacyReady(() => {
        const engine = new HarmonyEngine();
        engine.init();
        window.harmonyApp = engine;
    });
});
